import { supabase, supabaseAdmin } from "../lib/supabaseClient";

type DatabaseProductRow = {
  id: string;
  name: string;
  description: string | null;
  category: string;
  image_url: string | null;
  ingredients: string[] | null;
  is_available: boolean;
  product_type: string | null;
  display_order: number | null;
  created_at: string;
  updated_at: string;
};

type DatabaseItemGroupRow = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

type DatabaseCategoryRow = {
  id: string;
  name: string;
  slug: string;
  display_order: number;
  is_active: boolean;
  group_id: string;
  parent_category_id: string | null;
  created_at: string;
  updated_at: string;
  item_groups?: {
    id: string;
    name: string;
    slug: string;
  } | null;
};

type DatabaseSizeTemplateRow = {
  id: string;
  name: string;
  slug: string;
  size_code: string;
  description: string | null;
  item_group_id: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

type DatabaseProductSizeRow = {
  id: string;
  product_id: string;
  name: string;
  size_code: string | null;
  size_template_id: string | null;
  dilim_points: number | null;
  price_multiplier: number | null;
  price_override: number | null;
  delivery_price_override: number | null;
  display_order: number;
  is_available: boolean;
  min_qty: number;
  max_qty: number;
  is_default: boolean;
  products: {
    name: string;
  } | null;
  size_templates?: {
    id: string;
    slug: string;
    name: string;
    size_code: string;
  } | null;
};

export interface ProductRecord {
  id: string;
  name: string;
  description: string | null;
  category: string;
  imageUrl: string | null;
  ingredients: string[];
  isAvailable: boolean;
  productType: string | null;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ItemGroupRecord {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryRecord {
  id: string;
  name: string;
  slug: string;
  displayOrder: number;
  isActive: boolean;
  groupId: string;
  groupName: string | null;
  groupSlug: string | null;
  parentCategoryId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SizeTemplateRecord {
  id: string;
  name: string;
  slug: string;
  sizeCode: string;
  description: string | null;
  itemGroupId: string | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProductSizeRecord {
  id: string;
  productId: string;
  productName: string;
  sizeName: string;
  sizeCode: string | null;
  sizeTemplateId: string | null;
  sizeTemplateSlug: string | null;
  dilimPoints: number;
  isDefault: boolean;
  priceOverride: number | null;
  deliveryPriceOverride: number | null;
  defaultPrice: number;
  isAvailable: boolean;
  displayOrder: number;
  minQuantity: number;
  maxQuantity: number;
}

const adminClient = supabaseAdmin ?? supabase;
const PRODUCT_SIZE_SELECT = `
  id,
  product_id,
  name,
  size_code,
  size_template_id,
  dilim_points,
  price_multiplier,
  price_override,
  delivery_price_override,
  display_order,
  is_available,
  min_qty,
  max_qty,
  is_default,
  products (
    name
  ),
  size_templates (
    id,
    slug,
    name,
    size_code
  )
`;
const PRODUCT_SIZE_SELECT_FALLBACK = `
  id,
  product_id,
  name,
  size_code,
  size_template_id,
  price_multiplier,
  price_override,
  delivery_price_override,
  display_order,
  is_available,
  min_qty,
  max_qty,
  is_default,
  products (
    name
  ),
  size_templates (
    id,
    slug,
    name,
    size_code
  )
`;

const isMissingProductSizeDilimPointsColumn = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const queryError = error as { code?: string; message?: string };
  return queryError.code === "42703" && (queryError.message ?? "").includes("product_sizes.dilim_points");
};
const mapProduct = (row: DatabaseProductRow): ProductRecord => ({
  id: row.id,
  name: row.name,
  description: row.description,
  category: row.category,
  imageUrl: row.image_url,
  ingredients: Array.isArray(row.ingredients) ? row.ingredients : [],
  isAvailable: Boolean(row.is_available),
  productType: row.product_type ?? null,
  displayOrder: Number(row.display_order ?? 0),
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapItemGroup = (row: DatabaseItemGroupRow): ItemGroupRecord => ({
  id: row.id,
  name: row.name,
  slug: row.slug,
  description: row.description,
  displayOrder: row.display_order,
  isActive: Boolean(row.is_active),
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapCategory = (row: DatabaseCategoryRow): CategoryRecord => ({
  id: row.id,
  name: row.name,
  slug: row.slug,
  displayOrder: row.display_order,
  isActive: Boolean(row.is_active),
  groupId: row.group_id,
  groupName: row.item_groups?.name ?? null,
  groupSlug: row.item_groups?.slug ?? null,
  parentCategoryId: row.parent_category_id,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapSizeTemplate = (row: DatabaseSizeTemplateRow): SizeTemplateRecord => ({
  id: row.id,
  name: row.name,
  slug: row.slug,
  sizeCode: row.size_code,
  description: row.description,
  itemGroupId: row.item_group_id,
  displayOrder: row.display_order,
  isActive: Boolean(row.is_active),
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapProductSize = (row: DatabaseProductSizeRow): ProductSizeRecord => {
  const override =
    row.price_override != null ? Number(row.price_override) : null;
  const deliveryOverride =
    row.delivery_price_override != null
      ? Number(row.delivery_price_override)
      : null;
  const fallbackPrice = override ?? 0;

  return {
    id: row.id,
    productId: row.product_id,
    productName: row.products?.name ?? "Ürün",
    sizeName: row.name,
    sizeCode: row.size_code,
    sizeTemplateId: row.size_template_id,
    sizeTemplateSlug: row.size_templates?.slug ?? null,
    dilimPoints: Number(row.dilim_points ?? 0),
    isDefault: Boolean(row.is_default),
    priceOverride: override,
    deliveryPriceOverride: deliveryOverride,
    defaultPrice: fallbackPrice,
    isAvailable: Boolean(row.is_available),
    displayOrder: row.display_order,
    minQuantity: row.min_qty,
    maxQuantity: row.max_qty,
  };
};

export async function listProducts(): Promise<ProductRecord[]> {
  const { data, error } = await adminClient
    .from("products")
    .select("*")
    .order("category", { ascending: true })
    .order("display_order", { ascending: true })
    .order("name", { ascending: true });

  if (error) {
    throw error;
  }

  return (data ?? []).map(mapProduct);
}

async function getNextProductDisplayOrder(category: string): Promise<number> {
  const { data, error } = await adminClient
    .from("products")
    .select("display_order")
    .eq("category", category)
    .order("display_order", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return Number((data as { display_order?: number | null } | null)?.display_order ?? 0) + 10;
}

export interface UpsertProductInput {
  id?: string;
  name: string;
  description?: string | null;
  category: string;
  imageUrl?: string | null;
  ingredients?: string[];
  isAvailable?: boolean;
  displayOrder?: number;
}

export async function upsertProduct(
  input: UpsertProductInput,
): Promise<ProductRecord> {
  const displayOrder =
    input.displayOrder ?? (!input.id ? await getNextProductDisplayOrder(input.category) : undefined);
  const payload = {
    name: input.name,
    description: input.description ?? null,
    category: input.category,
    image_url: input.imageUrl ?? null,
    ingredients: input.ingredients ?? [],
    is_available: input.isAvailable ?? true,
    ...(displayOrder !== undefined ? { display_order: displayOrder } : {}),
  };

  const { data, error } = await adminClient
    .from("products")
    .upsert(input.id ? [{ ...payload, id: input.id }] : [payload], {
      onConflict: "id",
      ignoreDuplicates: false,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  return mapProduct(data as DatabaseProductRow);
}

export async function updateProductDisplayOrders(
  updates: Array<{ id: string; displayOrder: number }>,
) {
  for (const update of updates) {
    const { error } = await adminClient
      .from("products")
      .update({
        display_order: update.displayOrder,
        updated_at: new Date().toISOString(),
      })
      .eq("id", update.id);

    if (error) {
      throw error;
    }
  }
}

export async function deleteProduct(productId: string) {
  const { error } = await adminClient.from("products").delete().eq("id", productId);
  if (error) {
    throw error;
  }
}

const PRODUCT_IMAGE_BUCKET = "product-images";

const sanitizeFileName = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

export async function uploadProductImage(file: File): Promise<string> {
  const bucket = PRODUCT_IMAGE_BUCKET;

  const extension = file.name.split(".").pop() ?? "jpg";
  const baseName = sanitizeFileName(file.name.replace(`.${extension}`, ""));
  const path = `products/${Date.now()}-${baseName}.${extension}`;

  const { data, error } = await adminClient.storage
    .from(bucket)
    .upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || undefined,
    });

  if (error) {
    throw error;
  }

  const { data: publicUrlData } = adminClient.storage
    .from(bucket)
    .getPublicUrl(data.path);

  if (!publicUrlData.publicUrl) {
    throw new Error("Dosya yüklendi ancak public URL alınamadı.");
  }

  return publicUrlData.publicUrl;
}

export async function setProductAvailability(
  productId: string,
  isAvailable: boolean,
) {
  const { error } = await adminClient
    .from("products")
    .update({ is_available: isAvailable, updated_at: new Date().toISOString() })
    .eq("id", productId);
  if (error) {
    throw error;
  }
}

export async function listProductSizes(): Promise<ProductSizeRecord[]> {
  const { data, error } = await adminClient
    .from("product_sizes")
    .select(PRODUCT_SIZE_SELECT)
    .order("product_id", { ascending: true })
    .order("display_order", { ascending: true });

  if (isMissingProductSizeDilimPointsColumn(error)) {
    const fallbackResult = await adminClient
      .from("product_sizes")
      .select(PRODUCT_SIZE_SELECT_FALLBACK)
      .order("product_id", { ascending: true })
      .order("display_order", { ascending: true });

    if (fallbackResult.error) {
      throw fallbackResult.error;
    }

    return (fallbackResult.data ?? []).map(
      (row) => mapProductSize(row as unknown as DatabaseProductSizeRow),
    );
  }

  if (error) {
    throw error;
  }

  return (data ?? []).map(
    (row) => mapProductSize(row as unknown as DatabaseProductSizeRow),
  );
}

export async function updateProductSizePrice(
  sizeId: string,
  priceOverride: number | null,
) {
  const { error } = await adminClient
    .from("product_sizes")
    .update({
      price_override: priceOverride,
    })
    .eq("id", sizeId);

  if (error) {
    throw error;
  }
}

export interface UpdateProductSizeInput {
  id: string;
  priceOverride?: number | null;
  deliveryPriceOverride?: number | null;
  dilimPoints?: number;
  isAvailable: boolean;
  isDefault?: boolean;
  displayOrder?: number;
  sizeTemplateId?: string | null;
}

export async function updateProductSizes(
  updates: UpdateProductSizeInput[],
) {
  if (updates.length === 0) {
    return;
  }

  const payload = updates.map((item) => ({
    id: item.id,
    price_override: item.priceOverride,
    delivery_price_override: item.deliveryPriceOverride,
    dilim_points: item.dilimPoints,
    is_available: item.isAvailable,
    is_default: item.isDefault ?? undefined,
    display_order: item.displayOrder,
    size_template_id: item.sizeTemplateId,
  }));

  for (const row of payload) {
    const { id, ...rest } = row;
    const values = Object.entries(rest).reduce<Record<string, unknown>>(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    if (Object.keys(values).length === 0) {
      continue;
    }

    const { error } = await adminClient
      .from("product_sizes")
      .update(values)
      .eq("id", id);

    if (isMissingProductSizeDilimPointsColumn(error) && "dilim_points" in values) {
      const fallbackValues = { ...values };
      delete fallbackValues.dilim_points;

      if (Object.keys(fallbackValues).length === 0) {
        continue;
      }

      const fallbackResult = await adminClient
        .from("product_sizes")
        .update(fallbackValues)
        .eq("id", id);

      if (fallbackResult.error) {
        throw fallbackResult.error;
      }

      continue;
    }

    if (error) {
      throw error;
    }
  }
}

export interface CreateProductSizeInput {
  productId: string;
  sizeTemplateId: string;
  priceOverride?: number | null;
  deliveryPriceOverride?: number | null;
  dilimPoints?: number;
  isDefault?: boolean;
  displayOrder?: number;
}

export async function createProductSize(
  input: CreateProductSizeInput,
): Promise<ProductSizeRecord> {
  const { data: template, error: templateError } = await adminClient
    .from("size_templates")
    .select("*")
    .eq("id", input.sizeTemplateId)
    .single();

  if (templateError || !template) {
    throw templateError ?? new Error("Size template not found");
  }

  const payload = {
    product_id: input.productId,
    name: template.name,
    size_code: template.size_code,
    size_template_id: template.id,
    dilim_points: input.dilimPoints ?? 0,
    price_multiplier: 1,
    price_override: input.priceOverride ?? null,
    delivery_price_override:
      input.deliveryPriceOverride === undefined
        ? input.priceOverride ?? null
        : input.deliveryPriceOverride,
    is_default: input.isDefault ?? false,
    is_available: true,
    display_order: input.displayOrder ?? template.display_order ?? 0,
    min_qty: 1,
    max_qty: 1,
  };

  const insertWithSelect = async (includeDilimPoints: boolean) => {
    if (includeDilimPoints) {
      return adminClient
        .from("product_sizes")
        .insert(payload)
        .select(PRODUCT_SIZE_SELECT)
        .single();
    }

    const fallbackPayload = { ...payload, dilim_points: undefined };
    return adminClient
      .from("product_sizes")
      .insert(fallbackPayload)
      .select(PRODUCT_SIZE_SELECT_FALLBACK)
      .single();
  };

  let { data, error } = await insertWithSelect(true);

  if (isMissingProductSizeDilimPointsColumn(error)) {
    const fallbackResult = await insertWithSelect(false);
    data = fallbackResult.data;
    error = fallbackResult.error;
  }

  if (error) {
    throw error;
  }

  return mapProductSize(data as unknown as DatabaseProductSizeRow);
}

export async function deleteProductSize(sizeId: string) {
  const { error } = await adminClient
    .from("product_sizes")
    .delete()
    .eq("id", sizeId);
  if (error) {
    throw error;
  }
}

export interface SizeSelectionInput {
  sizeTemplateId: string;
  priceOverride: number | null;
  deliveryPriceOverride: number | null;
  isDefault?: boolean;
}

export async function syncProductSizesForTemplates(
  productId: string,
  selections: SizeSelectionInput[],
) {
  const { data: existing, error: existingError } = await adminClient
    .from("product_sizes")
    .select("id, size_template_id, is_default")
    .eq("product_id", productId);

  if (existingError) {
    throw existingError;
  }

  const existingByTemplate = new Map<string, { id: string; is_default: boolean }>();
  (existing ?? []).forEach((row) => {
    if (row.size_template_id) {
      existingByTemplate.set(row.size_template_id, { id: row.id, is_default: row.is_default ?? false });
    }
  });

  const selectedTemplateIds = new Set(
    selections.map((selection) => selection.sizeTemplateId).filter((id): id is string => Boolean(id)),
  );

  const deleteIds = (existing ?? [])
    .filter((row) => row.size_template_id && !selectedTemplateIds.has(row.size_template_id))
    .map((row) => row.id);

  if (deleteIds.length > 0) {
    const { error: deleteError } = await adminClient
      .from("product_sizes")
      .delete()
      .in("id", deleteIds);
    if (deleteError) {
      throw deleteError;
    }
  }

  const updates: UpdateProductSizeInput[] = [];
  const creations: SizeSelectionInput[] = [];

  selections.forEach((selection, index) => {
    const existingRow = existingByTemplate.get(selection.sizeTemplateId);
    const isDefault = selection.isDefault ?? index === 0;
    if (existingRow) {
      updates.push({
        id: existingRow.id,
        priceOverride: selection.priceOverride,
        deliveryPriceOverride: selection.deliveryPriceOverride,
        isAvailable: true,
        isDefault,
      });
    } else {
      creations.push({
        sizeTemplateId: selection.sizeTemplateId,
        priceOverride: selection.priceOverride,
        deliveryPriceOverride: selection.deliveryPriceOverride,
        isDefault,
      });
    }
  });

  if (updates.length > 0) {
    await updateProductSizes(updates);
  }

  for (const creation of creations) {
    await createProductSize({
      productId,
      sizeTemplateId: creation.sizeTemplateId,
      priceOverride: creation.priceOverride,
      deliveryPriceOverride: creation.deliveryPriceOverride,
      isDefault: creation.isDefault,
    });
  }
}

export async function listItemGroups(): Promise<ItemGroupRecord[]> {
  const { data, error } = await adminClient
    .from("item_groups")
    .select("*")
    .order("display_order", { ascending: true });

  if (error) {
    throw error;
  }

  return (data ?? []).map((row) =>
    mapItemGroup(row as DatabaseItemGroupRow),
  );
}

export interface UpsertItemGroupInput {
  id?: string;
  name: string;
  slug: string;
  description?: string | null;
  displayOrder: number;
  isActive?: boolean;
}

export async function upsertItemGroup(
  input: UpsertItemGroupInput,
): Promise<ItemGroupRecord> {
  const payload = {
    name: input.name,
    slug: input.slug,
    description: input.description ?? null,
    display_order: input.displayOrder,
    is_active: input.isActive ?? true,
  };

  const { data, error } = await adminClient
    .from("item_groups")
    .upsert(input.id ? [{ ...payload, id: input.id }] : [payload], {
      onConflict: "id",
      ignoreDuplicates: false,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  return mapItemGroup(data as DatabaseItemGroupRow);
}

export async function deleteItemGroup(groupId: string) {
  const { count, error: usageError } = await adminClient
    .from("menu_categories")
    .select("*", { count: "exact", head: true })
    .eq("group_id", groupId);

  if (usageError) {
    throw usageError;
  }

  if ((count ?? 0) > 0) {
    throw new Error(
      "Bu gruba bağlı kategoriler bulunuyor. Önce kategorileri farklı bir gruba taşıyın.",
    );
  }

  const { error } = await adminClient.from("item_groups").delete().eq("id", groupId);
  if (error) {
    throw error;
  }
}

export async function listCategories(): Promise<CategoryRecord[]> {
  const { data, error } = await adminClient
    .from("menu_categories")
    .select(
      `
        *,
        item_groups (
          id,
          name,
          slug
        )
      `,
    )
    .order("display_order", { ascending: true });

  if (error) {
    throw error;
  }

  return (data ?? []).map((row) =>
    mapCategory(row as DatabaseCategoryRow),
  );
}

export interface UpsertCategoryInput {
  id?: string;
  name: string;
  slug: string;
  displayOrder: number;
  groupId: string;
  parentCategoryId?: string | null;
  isActive?: boolean;
}

export async function upsertCategory(
  input: UpsertCategoryInput,
): Promise<CategoryRecord> {
  const payload = {
    name: input.name,
    slug: input.slug,
    display_order: input.displayOrder,
    group_id: input.groupId,
    parent_category_id: input.parentCategoryId ?? null,
    is_active: input.isActive ?? true,
  };

  const { data, error } = await adminClient
    .from("menu_categories")
    .upsert(input.id ? [{ ...payload, id: input.id }] : [payload], {
      onConflict: "id",
      ignoreDuplicates: false,
    })
    .select(
      `
        *,
        item_groups (
          id,
          name,
          slug
        )
      `,
    )
    .single();

  if (error) {
    throw error;
  }

  return mapCategory(data as DatabaseCategoryRow);
}

export async function deleteCategory(categoryId: string) {
  const { count, error: usageError } = await adminClient
    .from("products")
    .select("*", { count: "exact", head: true })
    .eq("category_id", categoryId);

  if (usageError) {
    throw usageError;
  }

  if ((count ?? 0) > 0) {
    const error = new Error(
      "Bu kategoriye bağlı ürünler bulundu. Önce ürünleri başka bir kategoriye taşıyın veya pasif hale getirin.",
    );
    (error as Error & { code?: string }).code = "CATEGORY_IN_USE";
    throw error;
  }

  const { error } = await adminClient
    .from("menu_categories")
    .delete()
    .eq("id", categoryId);
  if (error) {
    throw error;
  }
}

export async function listSizeTemplates(): Promise<SizeTemplateRecord[]> {
  const { data, error } = await adminClient
    .from("size_templates")
    .select("*")
    .order("display_order", { ascending: true });

  if (error) {
    throw error;
  }

  return (data ?? []).map((row) =>
    mapSizeTemplate(row as DatabaseSizeTemplateRow),
  );
}
